import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProductById, createProduct, updateProduct, uploadProductImage, getCategories, getUnits, getTaxGroups,
  getProductQuantityDiscounts, createProductQuantityDiscount, updateProductQuantityDiscount, deleteProductQuantityDiscount,
  getAssetUrl,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("products");

  // DISCOUNT_TYPE_OPTIONS needs t(), so it's built here rather than as a
  // module-level constant (a module-level array has no hook access).
  const DISCOUNT_TYPE_OPTIONS = [
    { value: "", label: t("discount_type_none", "No discount") },
    { value: "percentage", label: t("discount_type_percentage", "Percentage") },
    { value: "fixed", label: t("discount_type_fixed", "Fixed amount") },
  ];

  const [activeTab, setActiveTab] = useState("details");

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [form, setForm] = useState({
    sku: "",
    barcode: "",
    name: "",
    category_id: "",
    unit_id: "",
    price: "",
    cost: "",
    tax_group_id: "",
    track_inventory: true,
    // reorder_point is a single PRODUCT-LEVEL "warn me when stock gets
    // this low" threshold (not per-branch) — see Product.ReorderPoint and
    // services.CheckLowStock. 0/blank means no low-stock alert for this
    // product; only checked at all while Track Inventory is on.
    reorder_point: "",
    is_active: true,
    allows_fractional_qty: false,
    // loyalty_points is a fixed bonus number of loyalty points this product
    // grants PER UNIT sold, additive on top of whatever the order already
    // earns from spend-based points — see Product.LoyaltyPoints and the
    // "Earn on Product" toggle on the Loyalty settings screen. 0 (blank)
    // means this product grants no bonus points of its own.
    loyalty_points: "",
    // Single-product discount — see models.Product.DiscountType. Takes
    // priority over this product's own Quantity Discounts (below), and
    // over any Sale that also includes this product.
    discount_type: "",
    discount_value: "",
    discount_start_date: "",
    discount_end_date: "",
  });
  // Optional alternate units this product can ALSO be sold in besides its
  // base Unit of Measure above — e.g. base "Piece" plus a "Box" row worth
  // 12 Piece each. Empty by default: a product with no rows here sells
  // only in its base unit, exactly like before this feature existed. See
  // docs/order-design.md and models.ProductUnit.
  const [productUnits, setProductUnits] = useState([]); // [{unit_id, conversion_factor}]
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // imagePreview shows either the existing product.image_url (edit mode) or
  // a local blob: preview of a freshly-picked file, before it's ever been
  // uploaded — imageFile is only set when the user just picked a NEW file
  // this session, which is what tells handleSubmit to actually call
  // uploadProductImage afterwards (see UploadProductImageHandler,
  // product_handler.go — its own multipart endpoint, separate from this
  // page's plain-JSON create/update call).
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // --- Quantity Discounts ("buy N or more" tiers) — a separate, already-
  // persisted resource (models.ProductQuantityDiscount) keyed off a real
  // product id, so this tab only works once the product has been saved at
  // least once (id is set). Mirrors CurrencyDenominations.js's add/edit/
  // delete-inline pattern.
  const [tiers, setTiers] = useState([]);
  const [newTier, setNewTier] = useState({ min_quantity: "", discount_type: "percentage", discount_value: "", start_date: "", end_date: "" });
  const [addingTier, setAddingTier] = useState(false);
  const [editingTierId, setEditingTierId] = useState(null);
  const [editTier, setEditTier] = useState({ min_quantity: "", discount_type: "percentage", discount_value: "", start_date: "", end_date: "" });
  const [savingTierEdit, setSavingTierEdit] = useState(false);
  const [tierModal, setTierModal] = useState({ isOpen: false, id: null });
  const [tierDeleteStatus, setTierDeleteStatus] = useState("idle");

  const fetchTiers = () => {
    if (!id) return;
    getProductQuantityDiscounts(token, id).then((res) => setTiers(res || [])).catch(() => notify.error(t("notify_load_tiers_failed", "Failed to load quantity discounts")));
  };

  useEffect(() => {
    getCategories(token).then(setCategories).catch(() => notify.error(t("notify_load_categories_failed", "Failed to load categories")));
    getUnits(token).then(setUnits).catch(() => notify.error(t("notify_load_units_failed", "Failed to load units")));

    getTaxGroups(token).then(setTaxGroups).catch(() => notify.error(t("notify_load_tax_groups_failed", "Failed to load tax groups")));

    if (id) {
      getProductById(token, id).then((p) => {
        setForm({
          sku: p.sku || "",
          barcode: p.barcode || "",
          name: p.name || "",
          category_id: p.category_id ? String(p.category_id) : "",
          unit_id: p.unit_id ? String(p.unit_id) : "",
          price: p.price ?? "",
          cost: p.cost ?? "",
          tax_group_id: p.tax_group_id ? String(p.tax_group_id) : "",
          track_inventory: p.track_inventory !== undefined ? p.track_inventory : true,
          reorder_point: p.reorder_point ?? "",
          is_active: p.is_active !== undefined ? p.is_active : true,
          allows_fractional_qty: p.allows_fractional_qty || false,
          loyalty_points: p.loyalty_points ?? "",
          discount_type: p.discount_type || "",
          discount_value: p.discount_value ?? "",
          discount_start_date: p.discount_start_date ? p.discount_start_date.slice(0, 10) : "",
          discount_end_date: p.discount_end_date ? p.discount_end_date.slice(0, 10) : "",
        });
        setProductUnits(
          (p.product_units || []).map((u) => ({
            unit_id: String(u.unit_id),
            conversion_factor: String(u.conversion_factor),
            is_larger_than_base: u.is_larger_than_base,
          }))
        );
        if (p.image_url) setImagePreview(p.image_url);
      }).catch(() => notify.error(t("notify_load_product_failed", "Failed to load product")));
      fetchTiers();
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const unitOptions = units.map((u) => ({ value: String(u.id), label: u.symbol ? `${u.name} (${u.symbol})` : u.name }));
  // Leaving this unset means the product is fully tax-exempt — see
  // docs/tax-design.md.
  const taxGroupOptions = [{ value: "", label: t("tax_group_none_option", "No tax (exempt)") }, ...taxGroups.map((g) => ({ value: String(g.id), label: g.name }))];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const addUnitRow = () => setProductUnits((prev) => [...prev, { unit_id: "", conversion_factor: "", is_larger_than_base: true }]);
  const updateUnitRow = (idx, patch) => setProductUnits((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeUnitRow = (idx) => setProductUnits((prev) => prev.filter((_, i) => i !== idx));
  // A unit already used as the base unit, or already picked in another
  // alternate-unit row, can't be picked again for this row.
  const unitOptionsFor = (idx) => {
    const usedElsewhere = new Set(productUnits.filter((_, i) => i !== idx).map((r) => r.unit_id).filter(Boolean));
    return unitOptions.filter((o) => o.value !== form.unit_id && !usedElsewhere.has(o.value));
  };

  // --- Quantity Discount tier handlers ---
  const handleAddTier = async () => {
    if (!newTier.min_quantity || Number(newTier.min_quantity) <= 0) {
      notify.error(t("validation_min_quantity_required", "Enter a minimum quantity greater than zero"));
      return;
    }
    if (!newTier.discount_value || Number(newTier.discount_value) <= 0) {
      notify.error(t("validation_discount_value_required", "Enter a discount value greater than zero"));
      return;
    }
    setAddingTier(true);
    try {
      await createProductQuantityDiscount(token, id, {
        min_quantity: Number(newTier.min_quantity),
        discount_type: newTier.discount_type,
        discount_value: Number(newTier.discount_value),
        start_date: newTier.start_date,
        end_date: newTier.end_date,
      });
      notify.success(t("notify_tier_added", "Quantity discount tier added"));
      setNewTier({ min_quantity: "", discount_type: "percentage", discount_value: "", start_date: "", end_date: "" });
      fetchTiers();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_tier_add_failed", "Failed to add tier"));
    } finally {
      setAddingTier(false);
    }
  };

  const startEditTier = (tier) => {
    setEditingTierId(tier.id);
    setEditTier({
      min_quantity: String(tier.min_quantity),
      discount_type: tier.discount_type,
      discount_value: String(tier.discount_value),
      start_date: tier.start_date ? tier.start_date.slice(0, 10) : "",
      end_date: tier.end_date ? tier.end_date.slice(0, 10) : "",
    });
  };
  const cancelEditTier = () => setEditingTierId(null);

  const saveEditTier = async (tierId) => {
    if (!editTier.min_quantity || Number(editTier.min_quantity) <= 0) {
      notify.error(t("validation_min_quantity_required", "Enter a minimum quantity greater than zero"));
      return;
    }
    if (!editTier.discount_value || Number(editTier.discount_value) <= 0) {
      notify.error(t("validation_discount_value_required", "Enter a discount value greater than zero"));
      return;
    }
    setSavingTierEdit(true);
    try {
      await updateProductQuantityDiscount(token, tierId, {
        min_quantity: Number(editTier.min_quantity),
        discount_type: editTier.discount_type,
        discount_value: Number(editTier.discount_value),
        start_date: editTier.start_date,
        end_date: editTier.end_date,
      });
      notify.success(t("notify_tier_updated", "Quantity discount tier updated"));
      setEditingTierId(null);
      fetchTiers();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_tier_update_failed", "Failed to update tier"));
    } finally {
      setSavingTierEdit(false);
    }
  };

  const handleDeleteTierClick = (tierId) => setTierModal({ isOpen: true, id: tierId });
  const confirmDeleteTier = async () => {
    try {
      setTierDeleteStatus("loading");
      await deleteProductQuantityDiscount(token, tierModal.id);
      setTierDeleteStatus("success");
      setTimeout(() => {
        setTierModal({ isOpen: false, id: null });
        setTierDeleteStatus("idle");
      }, 1000);
      notify.success(t("notify_tier_deleted", "Quantity discount tier deleted"));
      fetchTiers();
    } catch (err) {
      setTierDeleteStatus("error");
      notify.error(err.data?.error || err.message || t("notify_tier_delete_failed", "Failed to delete tier"));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Product name is required");
    if (!form.sku.trim()) newErrors.sku = t("validation_sku_required", "SKU is required");
    if (!form.category_id) newErrors.category_id = t("validation_category_required", "Category is required");
    if (!form.unit_id) newErrors.unit_id = t("validation_unit_required", "Unit of measure is required");
    if (form.price !== "" && Number(form.price) < 0) newErrors.price = t("validation_price_negative", "Price cannot be negative");
    if (form.cost !== "" && Number(form.cost) < 0) newErrors.cost = t("validation_cost_negative", "Cost cannot be negative");
    if (form.reorder_point !== "" && Number(form.reorder_point) < 0) newErrors.reorder_point = t("validation_reorder_negative", "Reorder point cannot be negative");
    if (form.loyalty_points !== "" && Number(form.loyalty_points) < 0) newErrors.loyalty_points = t("validation_loyalty_negative", "Loyalty points cannot be negative");
    if (form.discount_type && (!form.discount_value || Number(form.discount_value) <= 0)) {
      newErrors.discount_value = t("validation_discount_value_required", "Enter a discount value greater than zero");
    }
    if (form.discount_type === "percentage" && Number(form.discount_value) > 100) {
      newErrors.discount_value = t("validation_discount_percent_max", "A percentage discount can't exceed 100");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      if (newErrors.discount_value) setActiveTab("discount");
      return;
    }

    // Rows the user started (picked a unit) must also have a valid
    // conversion factor — a fully empty trailing row is just dropped.
    const startedUnitRows = productUnits.filter((r) => r.unit_id);
    for (const r of startedUnitRows) {
      if (!r.conversion_factor || Number(r.conversion_factor) <= 0) {
        notify.error(t("validation_unit_conversion_required", "Every alternate unit needs a conversion factor greater than zero"));
        return;
      }
    }

    setLoading(true);
    const payload = {
      sku: form.sku.trim(),
      barcode: form.barcode.trim(),
      name: form.name.trim(),
      category_id: Number(form.category_id),
      unit_id: Number(form.unit_id),
      price: form.price === "" ? 0 : Number(form.price),
      cost: form.cost === "" ? 0 : Number(form.cost),
      tax_group_id: form.tax_group_id ? Number(form.tax_group_id) : 0,
      track_inventory: form.track_inventory,
      reorder_point: form.reorder_point === "" ? 0 : Number(form.reorder_point),
      is_active: form.is_active,
      allows_fractional_qty: form.allows_fractional_qty,
      loyalty_points: form.loyalty_points === "" ? 0 : Number(form.loyalty_points),
      discount_type: form.discount_type,
      discount_value: form.discount_type ? Number(form.discount_value) : 0,
      discount_start_date: form.discount_type ? form.discount_start_date : "",
      discount_end_date: form.discount_type ? form.discount_end_date : "",
      units: startedUnitRows.map((r) => ({
        unit_id: Number(r.unit_id),
        conversion_factor: Number(r.conversion_factor),
        is_larger_than_base: r.is_larger_than_base !== false,
      })),
    };

    try {
      let productId = id;
      if (id) {
        await updateProduct(token, id, payload);
        notify.success(t("notify_updated", "Product updated successfully"));
      } else {
        const created = await createProduct(token, payload);
        productId = created.id;
        notify.success(t("notify_created", "Product created successfully"));
      }

      // The picture is its own upload, separate from the JSON payload above
      // (see uploadProductImage/UploadProductImageHandler) — only fired
      // when the user actually picked a new file this session, and only
      // once productId is known (freshly created, or the one being edited).
      if (imageFile && productId) {
        try {
          await uploadProductImage(token, productId, imageFile);
        } catch (imgErr) {
          notify.error(imgErr.data?.error || imgErr.message || t("notify_image_upload_failed", "Product saved, but the image failed to upload"));
        }
      }

      // A brand-new product has no id yet until this point, so Quantity
      // Discount tiers couldn't be added during this same visit — send the
      // cashier back into edit mode for it instead of away to the list, so
      // they can immediately switch to that tab if they need tiers too.
      if (!id && productId) {
        navigate(`/edit-product/${productId}`);
      } else if (id) {
        fetchTiers();
      } else {
        navigate("/products");
      }
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { key: "details", label: t("tab_details", "Details") },
    { key: "discount", label: t("tab_discount", "Discount") },
    { key: "quantity", label: t("tab_quantity_discounts", "Quantity Discounts") },
  ];

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <ConfirmModal
        isOpen={tierModal.isOpen}
        title={t("delete_tier_confirm_title", "Delete Quantity Discount Tier")}
        message={t("delete_tier_confirm_message", "Are you sure you want to remove this tier?")}
        onConfirm={confirmDeleteTier}
        onCancel={() => setTierModal({ isOpen: false, id: null })}
        isLoading={tierDeleteStatus === "loading"}
        isSuccess={tierDeleteStatus === "success"}
        isError={tierDeleteStatus === "error"}
      />

      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/products")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Product") : t("add_title", "Add New Product")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Stock is managed separately, from the Inventory page")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="flex gap-2 mb-6 border-b border-gray-100 pb-4">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                activeTab === tab.key ? "bg-[var(--primary-color)] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "details" && (
        <div className="space-y-6">
          <div className="flex flex-col items-center pb-2">
            <div className="relative mb-3">
              <div className="w-32 h-32 rounded-[2rem] overflow-hidden ring-8 ring-gray-50 bg-gray-100 flex items-center justify-center transition-all hover:ring-[var(--primary-color)]/10">
                {imagePreview ? (
                  <img
                    src={getAssetUrl(imagePreview)}
                    alt="Product"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ICONS.Products className="w-12 h-12 text-gray-300" />
                )}
              </div>
              <label className="absolute bottom-1 right-1 bg-[var(--primary-color)] text-white p-2.5 rounded-2xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                <ICONS.Plus className="w-4 h-4" />
                <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
              </label>
            </div>
            <p className="text-[10px] text-gray-400 font-medium">{t("image_hint", "PNG, JPG, GIF or WEBP. Max 5MB.")}</p>
            {imagePreview && (
              <button
                type="button"
                onClick={() => { setImageFile(null); setImagePreview(null); }}
                className="mt-2 text-[10px] font-black uppercase text-red-400 hover:text-red-600 transition-colors"
              >
                {t("btn_remove_photo", "Remove Photo")}
              </button>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "Product Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Products} />
            </div>
            <EntityTranslationsButton entityType="product" entityId={id} defaultName={form.name} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_sku_label", "SKU")} value={form.sku} onChange={setField("sku")} error={errors.sku} />
            <FloatingInput label={t("field_barcode_label", "Barcode (optional)")} value={form.barcode} onChange={setField("barcode")} />
          </div>

          <div>
            <SelectBox
              label={t("col_category", "Category")}
              options={categoryOptions}
              value={form.category_id}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, category_id: val }));
                if (errors.category_id) setErrors((prev) => ({ ...prev, category_id: undefined }));
              }}
            />
            {errors.category_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.category_id}</p>}
          </div>

          <div>
            <SelectBox
              label={t("field_unit_label", "Unit of Measure")}
              options={unitOptions}
              value={form.unit_id}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, unit_id: val }));
                if (errors.unit_id) setErrors((prev) => ({ ...prev, unit_id: undefined }));
              }}
            />
            {errors.unit_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.unit_id}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_price_label", "Price")} type="number" value={form.price} onChange={setField("price")} error={errors.price} />
            <FloatingInput label={t("field_cost_label", "Cost")} type="number" value={form.cost} onChange={setField("cost")} error={errors.cost} />
          </div>

          <SelectBox
            label={t("field_tax_group_label", "Tax Group")}
            options={taxGroupOptions}
            value={form.tax_group_id}
            onChange={(val) => setForm((prev) => ({ ...prev, tax_group_id: val }))}
          />

          <ToggleSwitch
            label={t("toggle_track_inventory", "Track Inventory")}
            checked={form.track_inventory}
            onChange={(val) => setForm((prev) => ({ ...prev, track_inventory: val }))}
          />
          {form.track_inventory && (
            <FloatingInput
              label={t("field_reorder_point_label", "Reorder Point (0 = no low-stock alert)")}
              type="number"
              min="0"
              value={form.reorder_point}
              onChange={setField("reorder_point")}
              error={errors.reorder_point}
              icon={ICONS.BellAlert}
            />
          )}
          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />
          <ToggleSwitch
            label={t("toggle_allow_fractional", "Allow Fractional Quantity (e.g. sell 0.5)")}
            checked={form.allows_fractional_qty}
            onChange={(val) => setForm((prev) => ({ ...prev, allows_fractional_qty: val }))}
          />
          <p className="text-[11px] text-gray-400">
            {t("loyalty_points_hint", "A fixed number of bonus points this product earns per unit sold, additive on top of spend-based points. 0 means this product grants no bonus points of its own.")}
          </p>
          <FloatingInput
            label={t("field_loyalty_points_label", "Loyalty Points")}
            type="number"
            min="0"
            value={form.loyalty_points}
            onChange={setField("loyalty_points")}
            error={errors.loyalty_points}
            icon={ICONS.BundleOffers}
          />

          <div className="pt-6 border-t border-gray-50">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wide">{t("alt_units_heading", "Alternate Selling Units (optional)")}</h3>
              <button type="button" onClick={addUnitRow} className="text-xs font-bold text-[var(--primary-color)] hover:underline">{t("btn_add_unit", "+ Add unit")}</button>
            </div>
            <p className="text-[11px] text-gray-400 mb-3">
              {t("alt_units_hint", "Sell this product in another unit too, besides its base unit above. Works both ways — e.g. base \"Box\" plus a smaller \"Piece\" (12 Piece per Box), or base \"Box\" plus a bigger \"Carton\" (1 Carton = 10 Box).")}
            </p>
            {productUnits.length === 0 && (
              <p className="text-xs text-gray-300 italic py-2">{t("alt_units_empty", "No alternate units — this product sells only in its base unit.")}</p>
            )}
            <div className="space-y-3">
              {productUnits.map((row, idx) => {
                const rowUnitName = units.find((u) => String(u.id) === row.unit_id)?.name || t("unit_fallback_this_unit", "this unit");
                const baseUnitName = units.find((u) => String(u.id) === form.unit_id)?.name || t("unit_fallback_base_unit", "the base unit");
                const isLarger = row.is_larger_than_base !== false;
                const factor = Number(row.conversion_factor);
                return (
                  <div key={idx} className="border border-gray-100 rounded-2xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <SelectBox
                          label={t("field_unit_row_label", "Unit")}
                          options={unitOptionsFor(idx)}
                          value={row.unit_id}
                          onChange={(val) => updateUnitRow(idx, { unit_id: val })}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUnitRow(idx)}
                        className="mt-4 p-2 text-gray-300 hover:text-red-500 shrink-0"
                      >
                        <ICONS.Close className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex rounded-xl border border-gray-100 overflow-hidden shrink-0">
                        <button
                          type="button"
                          onClick={() => updateUnitRow(idx, { is_larger_than_base: true })}
                          className={`px-3 py-2 text-xs font-bold transition-colors ${
                            isLarger ? "bg-[var(--primary-color)] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          {t("unit_bigger", "Bigger")}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateUnitRow(idx, { is_larger_than_base: false })}
                          className={`px-3 py-2 text-xs font-bold transition-colors ${
                            !isLarger ? "bg-[var(--primary-color)] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          {t("unit_smaller", "Smaller")}
                        </button>
                      </div>
                      <div className="flex-1">
                        <FloatingInput
                          label={
                            isLarger
                              ? t("unit_conversion_label_bigger", "1 {row} = how many {base}").replace("{row}", rowUnitName).replace("{base}", baseUnitName)
                              : t("unit_conversion_label_smaller", "how many {row} per 1 {base}").replace("{row}", rowUnitName).replace("{base}", baseUnitName)
                          }
                          type="number"
                          value={row.conversion_factor}
                          onChange={(e) => updateUnitRow(idx, { conversion_factor: e.target.value })}
                        />
                      </div>
                    </div>
                    {factor > 0 && (
                      <p className="text-[11px] text-gray-400">
                        {isLarger
                          ? t("unit_conversion_summary_bigger", "1 {row} = {factor} {base}").replace("{row}", rowUnitName).replace("{factor}", factor).replace("{base}", baseUnitName)
                          : t("unit_conversion_summary_smaller", "{factor} {row} = 1 {base}").replace("{factor}", factor).replace("{row}", rowUnitName).replace("{base}", baseUnitName)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {activeTab === "discount" && (
        <div className="space-y-6">
          <p className="text-[11px] text-gray-400">
            {t("discount_tab_hint", "A discount set here applies to every sale of this product, takes priority over its own Quantity Discounts below, and over any Sale that also includes it. Leave \"No discount\" selected for none.")}
          </p>
          <SelectBox
            label={t("field_discount_type_label", "Discount Type")}
            options={DISCOUNT_TYPE_OPTIONS}
            value={form.discount_type}
            onChange={(val) => setForm((prev) => ({ ...prev, discount_type: val }))}
          />
          {form.discount_type && (
            <>
              <FloatingInput
                label={form.discount_type === "percentage" ? t("field_discount_percent_label", "Discount Percent") : t("field_discount_amount_label", "Discount Amount")}
                type="number"
                value={form.discount_value}
                onChange={setField("discount_value")}
                error={errors.discount_value}
              />
              <div className="grid grid-cols-2 gap-4">
                <FloatingInput label={t("field_start_date_label", "Start Date (optional)")} type="date" value={form.discount_start_date} onChange={setField("discount_start_date")} />
                <FloatingInput label={t("field_end_date_label", "End Date (optional)")} type="date" value={form.discount_end_date} onChange={setField("discount_end_date")} />
              </div>
              <p className="text-[10px] text-gray-400 ms-2 italic">{t("discount_dates_hint", "* Leave both dates blank for a discount that never expires.")}</p>
            </>
          )}
        </div>
        )}

        {activeTab === "quantity" && (
        <div className="space-y-6">
          {!id ? (
            <p className="text-sm text-gray-400 italic text-center py-8">
              {t("quantity_save_first_hint", "Save this product first, then come back here to add \"buy N or more\" quantity discount tiers.")}
            </p>
          ) : (
            <>
              <p className="text-[11px] text-gray-400">
                {t("quantity_tab_hint", "Each tier discounts every unit once the ordered quantity reaches its minimum. The highest qualifying tier wins — these are ignored entirely whenever this product's own single Discount (above) is active.")}
              </p>
              <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FloatingInput label={t("field_min_quantity_label", "Min Quantity")} type="number" value={newTier.min_quantity} onChange={(e) => setNewTier((p) => ({ ...p, min_quantity: e.target.value }))} />
                  <SelectBox
                    label={t("field_discount_type_label", "Discount Type")}
                    options={[{ value: "percentage", label: t("discount_type_percentage", "Percentage") }, { value: "fixed", label: t("discount_type_fixed_per_unit", "Fixed amount / unit") }]}
                    value={newTier.discount_type}
                    onChange={(val) => setNewTier((p) => ({ ...p, discount_type: val }))}
                  />
                </div>
                <FloatingInput label={t("field_discount_value_label", "Discount Value")} type="number" value={newTier.discount_value} onChange={(e) => setNewTier((p) => ({ ...p, discount_value: e.target.value }))} />
                <div className="grid grid-cols-2 gap-3">
                  <FloatingInput label={t("field_start_date_label", "Start Date (optional)")} type="date" value={newTier.start_date} onChange={(e) => setNewTier((p) => ({ ...p, start_date: e.target.value }))} />
                  <FloatingInput label={t("field_end_date_label", "End Date (optional)")} type="date" value={newTier.end_date} onChange={(e) => setNewTier((p) => ({ ...p, end_date: e.target.value }))} />
                </div>
                <Button variant="primary" icon={ICONS.Plus} onClick={handleAddTier} loading={addingTier} className="w-full">{t("btn_add_tier", "Add Tier")}</Button>
              </div>

              {tiers.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-8">{t("tier_empty", "No quantity discount tiers yet.")}</p>
              ) : (
                <div className="space-y-2">
                  {tiers.map((tier) => (
                    <div key={tier.id} className="border border-gray-100 rounded-2xl p-3">
                      {editingTierId === tier.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <input type="number" value={editTier.min_quantity} onChange={(e) => setEditTier((p) => ({ ...p, min_quantity: e.target.value }))}
                              className="text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none" placeholder={t("field_min_quantity_label", "Min Quantity")} />
                            <select value={editTier.discount_type} onChange={(e) => setEditTier((p) => ({ ...p, discount_type: e.target.value }))}
                              className="text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none">
                              <option value="percentage">{t("discount_type_percentage", "Percentage")}</option>
                              <option value="fixed">{t("discount_type_fixed_per_unit", "Fixed amount / unit")}</option>
                            </select>
                          </div>
                          <input type="number" value={editTier.discount_value} onChange={(e) => setEditTier((p) => ({ ...p, discount_value: e.target.value }))}
                            className="w-full text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none" placeholder={t("field_discount_value_label", "Discount Value")} />
                          <div className="grid grid-cols-2 gap-3">
                            <input type="date" value={editTier.start_date} onChange={(e) => setEditTier((p) => ({ ...p, start_date: e.target.value }))}
                              className="text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none" />
                            <input type="date" value={editTier.end_date} onChange={(e) => setEditTier((p) => ({ ...p, end_date: e.target.value }))}
                              className="text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none" />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="primary" size="sm" onClick={() => saveEditTier(tier.id)} loading={savingTierEdit}>{tCommon("btn_save", "Save")}</Button>
                            <Button variant="secondary" size="sm" onClick={cancelEditTier}>{tCommon("btn_cancel", "Cancel")}</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="font-bold text-sm text-gray-800">
                              {t("tier_summary_line", "{qty}+ units → {value}")
                                .replace("{qty}", tier.min_quantity)
                                .replace(
                                  "{value}",
                                  tier.discount_type === "percentage"
                                    ? t("tier_value_percentage", "{value}%").replace("{value}", tier.discount_value)
                                    : t("tier_value_fixed", "{value} off/unit").replace("{value}", tier.discount_value)
                                )}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {tier.start_date || tier.end_date
                                ? t("tier_date_range", "{start} — {end}")
                                    .replace("{start}", tier.start_date ? new Date(tier.start_date).toLocaleDateString() : t("tier_date_unknown", "…"))
                                    .replace("{end}", tier.end_date ? new Date(tier.end_date).toLocaleDateString() : t("tier_date_unknown", "…"))
                                : t("tier_always_on", "Always on")}
                            </div>
                          </div>
                          <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => startEditTier(tier)} />
                          <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteTierClick(tier.id)} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        )}

        <div className="flex gap-4 pt-6 mt-6 border-t border-gray-50">
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            className="flex-grow shadow-lg shadow-blue-500/10"
            icon={id ? ICONS.Save : ICONS.Plus}
          >
            {id ? t("btn_update_product", "Update Product") : t("btn_create_product", "Create Product")}
          </Button>
          <Button variant="secondary" onClick={() => navigate("/products")}>{tCommon("btn_cancel", "Cancel")}</Button>
        </div>
      </div>
    </div>
  );
}
